/**
 * Definitions for each CityScope Communities area.
 *
 * postcodes: Edinburgh outcode prefixes that map to this area.  The outcode is
 * the first part of a UK postcode (e.g. "EH6" from "EH6 5DT").  We match
 * application addresses against these using a simple regex extraction.
 *
 * A single outcode can appear in multiple areas (e.g. EH3 spans both New Town
 * and Stockbridge).  When that happens both areas will count the same
 * application — which is intentional, as residents of either area care about it.
 */
import { extractPostcodePrefix } from '../search/searchUtils';

export interface AreaDefinition {
  slug: string;
  name: string;
  postcodes: string[];
  description: string;
  wardArea: string;
  wardNames?: string[];
  constituencyNames?: string[];
  statusBadge: string;
  statusColor: 'amber' | 'red' | 'blue' | 'green' | 'purple' | 'gray';
  featured?: boolean;
  /** Approximate geographic centre [lng, lat] */
  center?: [number, number];
}

export const communityAreas: AreaDefinition[] = [
  {
    slug: 'leith',
    name: 'Leith',
    postcodes: ['EH6', 'EH7'],
    center: [-3.1728, 55.9757],
    description:
      'Vibrant waterfront neighbourhood with major regeneration activity, high short-term let applications and a dense commercial corridor along Leith Walk.',
    wardArea: 'Leith Walk / Leith',
    wardNames: ['Leith', 'Leith Walk'],
    constituencyNames: ['Edinburgh North and Leith'],
    statusBadge: 'Very active',
    statusColor: 'amber',
    featured: true,
  },
  {
    slug: 'portobello',
    name: 'Portobello',
    postcodes: ['EH15'],
    center: [-3.1108, 55.9538],
    description:
      'Seaside suburb with a protected town centre and growing pressure from holiday let applications along the promenade.',
    wardArea: 'Portobello / Craigmillar',
    wardNames: ['Portobello/Craigmillar', 'Portobello / Craigmillar'],
    constituencyNames: ['Edinburgh East and Musselburgh', 'Edinburgh East'],
    statusBadge: 'Short-let activity',
    statusColor: 'purple',
    featured: true,
  },
  {
    slug: 'new-town',
    name: 'New Town',
    postcodes: ['EH1', 'EH2', 'EH3'],
    center: [-3.1942, 55.9568],
    description:
      'UNESCO World Heritage Site with strict conservation requirements. High rate of listed building consent and advertisement applications.',
    wardArea: 'City Centre',
    wardNames: ['City Centre'],
    constituencyNames: ['Edinburgh East and Musselburgh', 'Edinburgh East'],
    statusBadge: 'Listed building focus',
    statusColor: 'blue',
    featured: true,
  },
  {
    slug: 'stockbridge',
    name: 'Stockbridge',
    postcodes: ['EH3', 'EH4'],
    center: [-3.2107, 55.9611],
    description:
      'Conservation area village within the city. Frequent applications for shopfront alterations, HMO licences and residential extensions.',
    wardArea: 'Inverleith',
    wardNames: ['Inverleith'],
    constituencyNames: ['Edinburgh North and Leith', 'Edinburgh West'],
    statusBadge: 'Conservation area',
    statusColor: 'green',
    featured: true,
  },
  {
    slug: 'marchmont-meadows',
    name: 'Marchmont & Meadows',
    postcodes: ['EH9'],
    center: [-3.1928, 55.9363],
    description:
      'Dense student and residential neighbourhood. High HMO application rate; strong community engagement around flatted developments.',
    wardArea: 'Meadows / Morningside',
    wardNames: ['Morningside'],
    constituencyNames: ['Edinburgh South'],
    statusBadge: 'HMO activity',
    statusColor: 'blue',
    featured: true,
  },
  {
    slug: 'gorgie-dalry',
    name: 'Gorgie / Dalry',
    postcodes: ['EH11'],
    center: [-3.2311, 55.9412],
    description:
      'Inner-west neighbourhood undergoing commercial and residential change. Several mixed-use and change-of-use applications active.',
    wardArea: 'Sighthill / Gorgie',
    wardNames: ['Sighthill/Gorgie', 'Sighthill / Gorgie'],
    constituencyNames: ['Edinburgh South West'],
    statusBadge: 'Change of use',
    statusColor: 'amber',
    featured: true,
  },
  {
    slug: 'corstorphine',
    name: 'Corstorphine',
    postcodes: ['EH12'],
    center: [-3.2842, 55.9448],
    description:
      'Leafy west Edinburgh suburb. Primarily residential extensions and householder applications, with some commercial activity near the zoo corridor.',
    wardArea: 'Corstorphine / Murrayfield',
    wardNames: ['Corstorphine/Murrayfield', 'Corstorphine / Murrayfield'],
    constituencyNames: ['Edinburgh West'],
    statusBadge: 'Quiet week',
    statusColor: 'gray',
    featured: false,
  },
  {
    slug: 'granton',
    name: 'Granton',
    postcodes: ['EH5'],
    center: [-3.2208, 55.9848],
    description:
      'Major waterfront regeneration zone. Multiple large-scale residential and mixed-use development applications at varying stages.',
    wardArea: 'Forth',
    wardNames: ['Forth'],
    constituencyNames: ['Edinburgh North and Leith'],
    statusBadge: 'Major projects',
    statusColor: 'red',
    featured: true,
  },
  {
    slug: 'morningside',
    name: 'Morningside',
    postcodes: ['EH10'],
    center: [-3.2141, 55.9245],
    description:
      'South Edinburgh suburb with a conservation-area high street. Applications typically involve shopfront changes, residential extensions, and flat conversions.',
    wardArea: 'Meadows / Morningside',
    wardNames: ['Morningside'],
    constituencyNames: ['Edinburgh South'],
    statusBadge: 'Steady',
    statusColor: 'green',
    featured: false,
  },
  {
    slug: 'south-queensferry',
    name: 'South Queensferry',
    postcodes: ['EH30'],
    center: [-3.3984, 55.9898],
    description:
      'Historic burgh on the Forth. Applications cluster around the High Street conservation area and outlying housing sites near the bridges.',
    wardArea: 'Almond',
    wardNames: ['Almond'],
    constituencyNames: ['Edinburgh West'],
    statusBadge: 'Low volume',
    statusColor: 'gray',
    featured: false,
  },
];

/** Postcode e.g. "EH6" extracted from an address string.  Returns null if none found. */
export function extractOutcode(address: string): string | null {
  return extractPostcodePrefix(address);
}

/** Whether a given address falls in the provided outcode list (case-insensitive). */
export function addressMatchesArea(address: string, postcodes: string[]): boolean {
  const outcode = extractOutcode(address);
  if (!outcode) return false;
  return postcodes.some((p) => p.toUpperCase() === outcode);
}

function normaliseBoundaryLabel(value: string | null | undefined): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/\s*\/\s*/g, '/')
    .replace(/[^a-z0-9/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function labelMatches(value: string | null | undefined, candidates: string[] | undefined): boolean {
  if (!value || !candidates?.length) return false;
  const normalisedValue = normaliseBoundaryLabel(value);
  return candidates.some((candidate) => {
    const normalisedCandidate = normaliseBoundaryLabel(candidate);
    return normalisedValue === normalisedCandidate;
  });
}

export interface AreaMatchMetadata {
  adminWard?: string | null;
  parliamentaryConstituency?: string | null;
}

export function metadataMatchesArea(
  area: AreaDefinition,
  metadata: AreaMatchMetadata | null | undefined
): boolean {
  if (!metadata) return false;
  if (metadata.adminWard) return labelMatches(metadata.adminWard, area.wardNames);
  return labelMatches(metadata.parliamentaryConstituency, area.constituencyNames);
}

export function metadataHasBoundary(metadata: AreaMatchMetadata | null | undefined): boolean {
  return Boolean(metadata?.adminWard || metadata?.parliamentaryConstituency);
}

export function getAreaBySlug(slug: string): AreaDefinition | undefined {
  return communityAreas.find((a) => a.slug === slug);
}
